/** What the command palette lists for a query - pure, so it is tested without a router or a DOM.
 *
 * **Same sources as the rail search, deliberately** (`searchTargets.ts`): the screens come from the
 * rail's own list (`navItems.ts`) plus the screens the person's menu reaches, Help sections from
 * `helpContent.ts`, and the last entry for a query is always "search the emissions register for
 * it". The palette therefore cannot say "no results" because a read failed - it does not read
 * anything - and it cannot name a screen the rail does not.
 *
 * It differs from the rail search in one way that matters: **it does not cap the screens**. The
 * rail's dropdown shows five because it sits in a 196px column; a palette that cut at five would
 * hide the screen a person typed the full name of whenever the first letters matched six others.
 * Matching is word-wise AND on lower-cased text, and ranking puts the screen whose *name starts
 * with* what was typed first, then one with a word that does, then any substring, then a match on
 * the group name alone ("trust" finds the four screens under Trust).
 */

import type { HelpHit } from "./helpContent";
import type { NavItem } from "./navItems";
import { searchTargets } from "./searchTargets";

export interface PaletteEntry {
  /** Stable within one list, for `aria-activedescendant` and React keys. */
  id: string;
  kind: "screen" | "help" | "register";
  label: string;
  /** Said under or beside the label: the group for a screen, the topic for a help section. */
  detail: string;
  to: string;
  /** The screen's icon. Only screens have one; a help hit and the register search do not. */
  nav?: NavItem;
  /** Help only: the `?tab=` to open. */
  tab?: string;
  /** Register only: the text to filter by. */
  q?: string;
}

export interface PaletteSection {
  heading: string;
  entries: PaletteEntry[];
}

function words(text: string): string[] {
  return text.toLowerCase().split(/\s+/).filter(Boolean);
}

/** Lower is better; `null` is "does not match". */
export function scoreOf(item: NavItem, needles: readonly string[]): number | null {
  const group = item.group.toLowerCase();
  const joined = needles.join(" ");
  // The screen's name and, where the tab strip calls it something else, that too: the better of the
  // two decides.
  let best: number | null = null;
  for (const text of [item.label, item.alias].filter((t): t is string => t !== undefined)) {
    const label = text.toLowerCase();
    if (!needles.every((needle) => label.includes(needle))) continue;
    let score = 3;
    if (label === joined) score = 0;
    else if (label.startsWith(joined)) score = 1;
    else {
      const labelWords = words(text);
      if (needles.every((needle) => labelWords.some((word) => word.startsWith(needle)))) score = 2;
    }
    best = best === null ? score : Math.min(best, score);
  }
  if (best !== null) return best;
  // Matched only once the group's name is allowed in: "targets" -> the Targets & plan screens.
  const label = item.label.toLowerCase();
  if (needles.every((needle) => label.includes(needle) || group.includes(needle))) return 4;
  return null;
}

/** The screens that match, best first; ties keep the rail's own order. */
export function rankScreens(items: readonly NavItem[], query: string): NavItem[] {
  const needles = words(query);
  if (needles.length === 0) return [...items];
  return items
    .map((item, order) => ({ item, order, score: scoreOf(item, needles) }))
    .filter((row): row is { item: NavItem; order: number; score: number } => row.score !== null)
    .sort((a, b) => a.score - b.score || a.order - b.order)
    .map((row) => row.item);
}

function screenEntry(item: NavItem): PaletteEntry {
  return {
    // Tabs of one place share a path, so the label keeps their ids apart (a duplicate React key otherwise).
    id: `screen:${item.to}:${item.label}`,
    kind: "screen",
    label: item.label,
    detail: item.group,
    to: item.to,
    nav: item,
  };
}

/** What the palette shows. A blank query lists every screen under its group, in rail order, so the
 * palette doubles as a map; a typed one lists matches, then Help, then the register search. */
export function paletteSections(
  query: string,
  screens: readonly NavItem[],
  help: readonly HelpHit[],
): PaletteSection[] {
  if (words(query).length === 0) {
    const sections: PaletteSection[] = [];
    for (const item of screens) {
      const last = sections[sections.length - 1];
      if (last && last.heading === item.group) last.entries.push(screenEntry(item));
      else sections.push({ heading: item.group, entries: [screenEntry(item)] });
    }
    return sections;
  }

  const sections: PaletteSection[] = [];
  const matched = rankScreens(screens, query);
  if (matched.length > 0) sections.push({ heading: "Screens", entries: matched.map(screenEntry) });

  // Help and the register come from the one function the rail search uses, given no screens: its
  // screen cap is the thing the palette does not want.
  const rest = searchTargets(query, [], help);
  const helpEntries = rest
    .filter((target) => target.kind === "help")
    .map(
      (target): PaletteEntry => ({
        id: `help:${target.tab ?? ""}:${target.label}`,
        kind: "help",
        label: target.label,
        detail: target.detail,
        to: target.to,
        tab: target.tab,
      }),
    );
  if (helpEntries.length > 0) sections.push({ heading: "Help", entries: helpEntries });

  const register = rest.find((target) => target.kind === "register");
  if (register) {
    sections.push({
      heading: "Records",
      entries: [
        {
          id: "register",
          kind: "register",
          label: register.label,
          detail: register.detail,
          to: register.to,
          q: register.q,
        },
      ],
    });
  }
  return sections;
}

export function flatten(sections: readonly PaletteSection[]): PaletteEntry[] {
  return sections.flatMap((section) => section.entries);
}

/** The shortcut as the person's keyboard spells it. The listener accepts either modifier on every
 * platform (`AppShell`), so this is only what the button *says*: a Windows user told to press a key
 * their keyboard does not have is a hint that makes the feature look broken. */
export function shortcutLabel(): string {
  try {
    const platform = (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData?.platform;
    return /mac|iphone|ipad/i.test(platform ?? navigator.platform ?? "") ? "⌘K" : "Ctrl K";
  } catch {
    return "Ctrl K";
  }
}
